/**
 * MudEditPanel — the editable mud section of a Daily Report (Parte Diario).
 * Lists the day's mud product transactions (scoped to `reportId`) with a per-row
 * Edit trigger and a "+ Agregar" trigger that both OPEN a stylized `<Modal>`
 * (the house rule: edits open in a modal, not an inline expanded row); writes go
 * to PUT (edit) / POST (add). The same pattern applies to mud volumes.
 *
 * Mud is DRILLING-ONLY input: when the event isn't a drilling event the backend
 * returns 422, so the add/edit controls are disabled and a "solo en eventos de
 * perforación" note is shown. The event type is resolved from the existing
 * tenant-wide events query.
 *
 * Reads: the day's transactions come from the existing GET /events/{id}/mud-checks
 * list (scoped to reportId). The contract exposes no read for mud volumes, so
 * volumes shown here are the rows created/edited in this session (seeded from the
 * write responses). After every write the mud list is refetched.
 */
import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { PicklistSelect } from "@components/ui/PicklistSelect";
import { useGetEventMudChecksQuery, useGetAllEventsQuery } from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { fmtMoney, fmtNum } from "@lib/format";
import {
  useCreateMudTransactionMutation,
  useUpdateMudTransactionMutation,
  useCreateMudVolumeMutation,
  useUpdateMudVolumeMutation,
  type MudTransactionWrite,
  type MudTransactionListRow,
  type MudVolumeWrite,
  type MudVolumeOut,
} from "./mudApi";

/** True when an RTK Query error is an HTTP 403 (forbidden). */
function isForbidden(err: unknown): boolean {
  return (
    !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 403
  );
}
/** True when an RTK Query error is an HTTP 422 (non-drilling / validation). */
function isUnprocessable(err: unknown): boolean {
  return (
    !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 422
  );
}

/** Parse a numeric input field: blank → null, else the parsed float (or null). */
function numOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
/** Trimmed string, or null when blank. */
function strOrNull(s: string): string | null {
  const v = s.trim();
  return v === "" ? null : v;
}

/** The editable transaction form shape (strings for the number inputs). */
interface TxnForm {
  product: string;
  received: string;
  used: string;
  adjusted: string;
  returned: string;
  cost: string;
  currency: string;
}

const EMPTY_TXN: TxnForm = {
  product: "",
  received: "",
  used: "",
  adjusted: "",
  returned: "",
  cost: "",
  currency: "",
};

function txnToForm(tran: MudTransactionListRow): TxnForm {
  return {
    product: tran.mud_product_id ?? "",
    received: tran.quantity_received?.toString() ?? "",
    used: tran.quantity_used?.toString() ?? "",
    adjusted: tran.quantity_adjusted?.toString() ?? "",
    returned: tran.quantity_returned?.toString() ?? "",
    cost: tran.product_daily_cost?.toString() ?? "",
    currency: tran.currency ?? "",
  };
}

function txnFormToBody(f: TxnForm): MudTransactionWrite {
  return {
    mud_product_id: strOrNull(f.product),
    quantity_received: numOrNull(f.received),
    quantity_used: numOrNull(f.used),
    quantity_adjusted: numOrNull(f.adjusted),
    quantity_returned: numOrNull(f.returned),
    product_daily_cost: numOrNull(f.cost),
    currency: strOrNull(f.currency),
  };
}

/** The editable volume form shape (strings for the number inputs). */
interface VolForm {
  location: string;
  volume: string;
  desc: string;
  seq: string;
}

const EMPTY_VOL: VolForm = {
  location: "",
  volume: "",
  desc: "",
  seq: "",
};

function volToForm(vol: MudVolumeOut): VolForm {
  return {
    location: vol.location_entry_key ?? "",
    volume: vol.mud_vol?.toString() ?? "",
    desc: vol.mud_vol_desc ?? "",
    seq: vol.sequence_no?.toString() ?? "",
  };
}

function volFormToBody(f: VolForm): MudVolumeWrite {
  return {
    location_entry_key: strOrNull(f.location),
    mud_vol: numOrNull(f.volume),
    mud_vol_desc: strOrNull(f.desc),
    sequence_no: numOrNull(f.seq),
  };
}

export function MudEditPanel({ reportId, eventId }: { reportId: string; eventId: string }) {
  const { t, locale } = useLocale();

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetEventMudChecksQuery(eventId, { skip: !eventId });

  // Resolve the event type from the existing tenant-wide events query to gate
  // the drilling-only input (backend 422 otherwise). While events load we don't
  // block optimistically — the backend remains the source of truth.
  const { data: allEvents } = useGetAllEventsQuery();
  const event = useMemo(
    () => (allEvents ?? []).find((e) => e.id === eventId),
    [allEvents, eventId],
  );
  const isDrilling = !event || event.event_type === "drilling";

  // Product catalog — used only to resolve labels in the read table rows; the
  // modal's product field submits a free id via PicklistSelect (which free-text
  // falls back when the mud_products catalog is empty, as on live Oxbo-M).
  const { data: productCat } = useGetResolvedCatalogQuery({ categoryKey: "mud_products" });
  const productEntries = productCat?.entries ?? [];
  const productLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of productEntries) m.set(e.entry_key, e.display_label);
    return m;
  }, [productEntries]);

  // Location catalog — likewise only for read-row label resolution.
  const { data: locationCat } = useGetResolvedCatalogQuery({ categoryKey: "mud_locations" });
  const locationEntries = locationCat?.entries ?? [];
  const locationLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of locationEntries) m.set(e.entry_key, e.display_label);
    return m;
  }, [locationEntries]);

  // The live P route carries entity_id/version on each list row (see
  // MudTransactionListRow); narrow to that so the Edit affordance has its key.
  const allTransactions = (
    Array.isArray(data?.transactions) ? (data!.transactions as MudTransactionListRow[]) : []
  ).filter((x) => x.entity_id != null);
  const transactions = allTransactions.filter((x) => x.daily_report_id === reportId);

  // The contract exposes no read for mud volumes, so we surface the volumes
  // touched in this session (created/updated), seeded from the write responses.
  const [volumes, setVolumes] = useState<MudVolumeOut[]>([]);
  const upsertVolume = (v: MudVolumeOut) =>
    setVolumes((prev) => {
      const i = prev.findIndex((x) => x.entity_id === v.entity_id);
      if (i === -1) return [...prev, v];
      const next = prev.slice();
      next[i] = v;
      return next;
    });

  // ── Transaction modal state (holds "new" for add, or an entity_id for edit) ─
  const [editingTxn, setEditingTxn] = useState<string | null>(null);
  const [txnForm, setTxnForm] = useState<TxnForm>(EMPTY_TXN);
  const [createTxn, createTxnState] = useCreateMudTransactionMutation();
  const [updateTxn, updateTxnState] = useUpdateMudTransactionMutation();
  const txnBusy = createTxnState.isLoading || updateTxnState.isLoading;
  const txnError = createTxnState.error ?? updateTxnState.error;

  function setTxnField<K extends keyof TxnForm>(k: K, v: string) {
    setTxnForm((f) => ({ ...f, [k]: v }));
  }
  function openAddTxn() {
    setTxnForm(EMPTY_TXN);
    setEditingTxn("new");
  }
  function openEditTxn(tran: MudTransactionListRow) {
    setTxnForm(txnToForm(tran));
    setEditingTxn(tran.entity_id);
  }
  async function submitTxn() {
    const body = txnFormToBody(txnForm);
    try {
      if (editingTxn === "new") {
        await createTxn({ reportId, body }).unwrap();
      } else if (editingTxn) {
        await updateTxn({ reportId, entityId: editingTxn, body }).unwrap();
      }
      setEditingTxn(null);
      refetch();
    } catch {
      /* error surfaced via txnError in the modal footer */
    }
  }

  // ── Volume modal state (holds "new" for add, or an entity_id for edit) ──────
  const [editingVol, setEditingVol] = useState<string | null>(null);
  const [volForm, setVolForm] = useState<VolForm>(EMPTY_VOL);
  const [createVol, createVolState] = useCreateMudVolumeMutation();
  const [updateVol, updateVolState] = useUpdateMudVolumeMutation();
  const volBusy = createVolState.isLoading || updateVolState.isLoading;
  const volError = createVolState.error ?? updateVolState.error;

  function setVolField<K extends keyof VolForm>(k: K, v: string) {
    setVolForm((f) => ({ ...f, [k]: v }));
  }
  function openAddVol() {
    setVolForm(EMPTY_VOL);
    setEditingVol("new");
  }
  function openEditVol(vol: MudVolumeOut) {
    setVolForm(volToForm(vol));
    setEditingVol(vol.entity_id);
  }
  async function submitVol() {
    const body = volFormToBody(volForm);
    try {
      let res: MudVolumeOut;
      if (editingVol === "new") {
        res = await createVol({ reportId, body }).unwrap();
      } else if (editingVol) {
        res = await updateVol({ reportId, entityId: editingVol, body }).unwrap();
      } else {
        return;
      }
      setEditingVol(null);
      upsertVolume(res);
      void refetch();
    } catch {
      /* error surfaced via volError in the modal footer */
    }
  }

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      {!isDrilling ? (
        <div className="empty-state" style={{ marginBottom: 12 }}>
          {t("mud.edit.drillingOnly")}
        </div>
      ) : null}

      {/* ── Transactions ─────────────────────────────────────────────── */}
      <div className="card">
        <h3>{t("mud.transactions")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("mud.col.product")}</th>
                <th className="num">{t("mud.col.received")}</th>
                <th className="num">{t("mud.col.used")}</th>
                <th className="num">{t("mud.col.adjusted")}</th>
                <th className="num">{t("mud.col.returned")}</th>
                <th className="num">{t("mud.col.dailyCost")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    {t("mud.transactions.empty")}
                  </td>
                </tr>
              ) : (
                transactions.map((tran) => (
                  <tr key={tran.entity_id} data-owner="p">
                    <td>
                      {tran.mud_product_id
                        ? (productLabels.get(tran.mud_product_id) ?? tran.mud_product_id)
                        : "—"}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(tran.quantity_received, locale, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(tran.quantity_used, locale, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(tran.quantity_adjusted, locale, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(tran.quantity_returned, locale, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="num tab-nums">
                      {tran.product_daily_cost != null
                        ? fmtMoney(tran.product_daily_cost, locale, {
                            currency: tran.currency ?? undefined,
                          })
                        : "—"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn"
                        disabled={!isDrilling}
                        onClick={() => openEditTxn(tran)}
                      >
                        {t("common.edit")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            className="btn primary"
            disabled={!isDrilling}
            onClick={openAddTxn}
          >
            {t("mud.add.transaction")}
          </button>
        </div>
      </div>

      {/* ── Volumes ──────────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("mud.volumes")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("mud.col.seq")}</th>
                <th>{t("mud.col.location")}</th>
                <th className="num">{t("mud.col.volume")}</th>
                <th>{t("mud.col.volDesc")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {volumes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    {t("mud.volumes.empty")}
                  </td>
                </tr>
              ) : (
                volumes.map((v) => (
                  <tr key={v.entity_id} data-owner="p">
                    <td className="num tab-nums">{v.sequence_no ?? "—"}</td>
                    <td>
                      {v.location_entry_key
                        ? (locationLabels.get(v.location_entry_key) ?? v.location_entry_key)
                        : "—"}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(v.mud_vol, locale, { maximumFractionDigits: 1 })}
                    </td>
                    <td>{v.mud_vol_desc ?? "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn"
                        disabled={!isDrilling}
                        onClick={() => openEditVol(v)}
                      >
                        {t("common.edit")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            className="btn primary"
            disabled={!isDrilling}
            onClick={openAddVol}
          >
            {t("mud.add.volume")}
          </button>
        </div>
      </div>

      {/* ── Transaction modal (one per section, add + edit) ──────────── */}
      <Modal
        open={editingTxn !== null}
        title={editingTxn === "new" ? t("mud.add.transaction") : t("common.edit")}
        onClose={() => setEditingTxn(null)}
        onConfirm={submitTxn}
        busy={txnBusy}
        confirmLabel={
          txnBusy
            ? t("common.saving")
            : editingTxn === "new"
              ? t("common.add")
              : t("common.save")
        }
        cancelLabel={t("common.cancel")}
        footerNote={txnError ? mutationErrorText(txnError, t) : null}
      >
        <div className="modal-grid">
          <PicklistSelect
            categoryKey="mud_products"
            label={t("mud.col.product")}
            value={txnForm.product}
            onChange={(v) => setTxnField("product", v)}
          />
          <label>
            <span>{t("mud.col.received")}</span>
            <input
              type="number"
              value={txnForm.received}
              onChange={(e) => setTxnField("received", e.target.value)}
            />
          </label>
          <label>
            <span>{t("mud.col.used")}</span>
            <input
              type="number"
              value={txnForm.used}
              onChange={(e) => setTxnField("used", e.target.value)}
            />
          </label>
          <label>
            <span>{t("mud.col.adjusted")}</span>
            <input
              type="number"
              value={txnForm.adjusted}
              onChange={(e) => setTxnField("adjusted", e.target.value)}
            />
          </label>
          <label>
            <span>{t("mud.col.returned")}</span>
            <input
              type="number"
              value={txnForm.returned}
              onChange={(e) => setTxnField("returned", e.target.value)}
            />
          </label>
          <label>
            <span>{t("mud.col.dailyCost")}</span>
            <input
              type="number"
              value={txnForm.cost}
              onChange={(e) => setTxnField("cost", e.target.value)}
            />
          </label>
          <label>
            <span>{t("mud.col.currency")}</span>
            <input
              type="text"
              value={txnForm.currency}
              onChange={(e) => setTxnField("currency", e.target.value)}
            />
          </label>
        </div>
      </Modal>

      {/* ── Volume modal (one per section, add + edit) ───────────────── */}
      <Modal
        open={editingVol !== null}
        title={editingVol === "new" ? t("mud.add.volume") : t("common.edit")}
        onClose={() => setEditingVol(null)}
        onConfirm={submitVol}
        busy={volBusy}
        confirmLabel={
          volBusy
            ? t("common.saving")
            : editingVol === "new"
              ? t("common.add")
              : t("common.save")
        }
        cancelLabel={t("common.cancel")}
        footerNote={volError ? mutationErrorText(volError, t) : null}
      >
        <div className="modal-grid">
          <label>
            <span>{t("mud.col.seq")}</span>
            <input
              type="number"
              value={volForm.seq}
              onChange={(e) => setVolField("seq", e.target.value)}
            />
          </label>
          <PicklistSelect
            categoryKey="mud_locations"
            label={t("mud.col.location")}
            value={volForm.location}
            onChange={(v) => setVolField("location", v)}
          />
          <label>
            <span>{t("mud.col.volume")}</span>
            <input
              type="number"
              value={volForm.volume}
              onChange={(e) => setVolField("volume", e.target.value)}
            />
          </label>
          <label className="modal-span-all">
            <span>{t("mud.col.volDesc")}</span>
            <input
              type="text"
              value={volForm.desc}
              onChange={(e) => setVolField("desc", e.target.value)}
            />
          </label>
        </div>
      </Modal>
    </QueryState>
  );
}

/** Map a mutation error to a localized message: 403 → admins/operators only,
 * 422 → drilling-events-only, else a generic save error. */
function mutationErrorText(error: unknown, t: (k: import("@i18n/messages").MessageKey) => string): string {
  if (isForbidden(error)) return t("mud.edit.forbidden");
  if (isUnprocessable(error)) return t("mud.edit.drillingOnly");
  return t("mud.edit.saveError");
}
